/**
 * The quiet rail: news as plain text on the page background — reference
 * material, deliberately not another card. Stories mentioning a tracked bill
 * are tagged with the bill number and sort first.
 */
import React, { useMemo } from 'react';
import type { NewsItem } from './useProHomeData';

const HEADING_CLASS =
  'text-[11.5px] font-bold uppercase tracking-[.13em] text-primary-400 dark:text-primary-dark-400';

interface QuietRailProps {
  news: NewsItem[];
  /** Bill numbers the user tracks — used to tag matching news stories. */
  trackedBillNumbers: string[];
}

const QuietRail: React.FC<QuietRailProps> = ({ news, trackedBillNumbers }) => {
  const taggedNews = useMemo(() => {
    const numbers = trackedBillNumbers.filter(Boolean);
    const tagged = news.map((item) => {
      const haystack = `${item.title} ${item.description ?? ''}`;
      const match = numbers.find((n) => haystack.includes(n)) ?? null;
      return { ...item, matchedBill: match };
    });
    return tagged.sort((a, b) => Number(b.matchedBill !== null) - Number(a.matchedBill !== null));
  }, [news, trackedBillNumbers]);

  return (
    <aside className="flex flex-col h-full min-h-0" aria-label="In the news">
      <div className="pb-2">
        <h2 className={HEADING_CLASS}>In the news</h2>
      </div>
      <div className="pro-home-scroll flex-1 min-h-0">
        {taggedNews.length === 0 ? (
          <p className="text-[12.5px] text-slate-400 dark:text-text-dark-tertiary py-1.5">
            No coverage matching your tracked bills.
          </p>
        ) : (
          taggedNews.map((item) => (
            <div
              key={item.id}
              className="py-2.5 border-b border-border-light dark:border-border-dark-hairline"
            >
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-[13px] font-semibold leading-[1.45] text-slate-700 dark:text-text-dark-secondary hover:text-primary-700 line-clamp-2"
              >
                {item.title}
              </a>
              <p className="mt-0.5 text-[11.5px] text-slate-400 dark:text-text-dark-tertiary truncate">
                {item.feedName} · {item.timeAgo}
                {item.matchedBill && (
                  <>
                    {' · '}
                    <span className="font-semibold text-primary-600 dark:text-primary-dark-500">
                      {item.matchedBill}
                    </span>
                  </>
                )}
              </p>
            </div>
          ))
        )}
      </div>
    </aside>
  );
};

export default QuietRail;
